import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
    import { THGActionType } from '@/enums/thg/THGActionType';
    import { useTHGUsersStore } from '@/storenew/thg/users';
    import { storeToRefs } from 'pinia';
    import { alertActionError, alertInfo, confirm } from '@/utils/dialogs';
    import { processActionResponse } from '@/utils/thg';
    import emitter from '@/utils/mitt';
    import { THGActionResponse } from '@/interfaces/thg/THGActionResponse';
    import { THGInfoMailPayload } from '@/interfaces/thg/THGInfoMailPayload';
    import {
        triggerAddTags,
        triggerInfoMailSending,
        triggerPromoPremiumPayout,
    } from '@/api/thg/actions/users';
    import { THGPromoPremiumPayoutPayload } from '@/interfaces/thg/THGPromoPremiumPayoutPayload';
    import { useTHGGlobalStore } from '@/storenew/thg/global';
    import { Country } from '@/enums/Country';
    import { ModalProgrammatic } from 'buefy';
    import THGTagSelectModal from '@/components/thg/THGTagSelectModal.vue';
    import { THGTag } from '@/interfaces/thg/THGTag';
    import { THGUserAddTagsPayload } from '@/interfaces/thg/THGUserAddTagsPayload';

    
export default /*#__PURE__*/_defineComponent({
  __name: 'THGUserActions',
  setup(__props) {

    const store = useTHGUsersStore();
    const { currentCountry } = storeToRefs(useTHGGlobalStore());
    const { filterParam } = storeToRefs(store);

    const chosenAction = ref<THGActionType | null>(null);
    const selectedTags = ref<THGTag[]>([]);

    const enableActionButton = computed<boolean>(() => {
        // enable action button only if action itself and a year is selected
        return Boolean(chosenAction.value && filterParam.value);
    });

    /**
     * Trigger the action.
     */
    async function apply() {
        const initialQuestion = `Soll die ausgewählte Aktion <strong>${(
            chosenAction.value as string
        ).toUpperCase()}</strong> wirklich gestartet werden?`;

        const { result: initialResult, dialog: initialDialog } = await confirm(
            'Aktion starten',
            initialQuestion,
            'Starten',
            'is-primary',
        );

        if (initialResult && chosenAction.value) {
            if (chosenAction.value === THGActionType.BULK_ADD_TAGS) {
                const result = await new Promise<THGTag[] | null>((resolve) => {
                    const modal = ModalProgrammatic.open({
                        component: THGTagSelectModal,
                        trapFocus: true,
                        hasModalCard: true,
                        events: {
                            select: (tags: THGTag[]) => {
                                resolve(tags);
                                modal.close();
                            },
                        },
                        onCancel: () => resolve(null),
                    });
                });

                if (!result || result.length === 0) {
                    initialDialog.close();
                    return;
                }

                selectedTags.value = result;
            }

            // start loading
            emitter.emit('showLoader');

            // make pretend action
            let pretendResponse: THGActionResponse;

            try {
                pretendResponse = await triggerAction(chosenAction.value);
            } catch (e) {
                alertActionError(e, initialDialog);
                return;
            }

            // hide the loader and close
            emitter.emit('hideLoader');
            initialDialog.close();

            const finalQuestion = `Die ausgewählte Aktion betrifft ${pretendResponse.users_count} Benutzer. Bitte
                                    vergleiche diese Anzahl mit der hier in der
                                    App angezeigten! Wirklich ausführen?`;

            const { result: finalResult, dialog: finalDialog } = await confirm(
                'Ausführung bestätigen',
                finalQuestion,
                'Ja, ausführen',
                'is-warning',
            );

            if (finalResult) {
                // start loading
                emitter.emit('showLoader');

                // trigger the 'real' action
                let finalResponse: THGActionResponse;

                try {
                    finalResponse = await triggerAction(chosenAction.value, false);
                } catch (e) {
                    alertActionError(e, finalDialog);
                    return;
                }

                // hide the loader and close
                emitter.emit('hideLoader');
                finalDialog.close();

                alertInfo(
                    `Aktion ${(chosenAction.value as string).toUpperCase()} ausgeführt`,
                    `Betroffene Benutzer: ${finalResponse.users_count}`,
                );
            }
        }
    }

    /**
     * Trigger the action via api by actionType.
     *
     * @param actionType
     * @param pretend
     */
    async function triggerAction(
        actionType: THGActionType,
        pretend = true,
    ): Promise<THGActionResponse> {
        if (!chosenAction.value) {
            throw new Error('no action selected');
        }

        if (actionType === THGActionType.PROMO_PREMIUM_PAYOUT) {
            const payload: THGPromoPremiumPayoutPayload = {
                pretend: pretend,
                filter_params: filterParam.value,
                action_type: chosenAction.value,
            };

            // trigger action via api
            const resp = await triggerPromoPremiumPayout(payload);

            return processActionResponse(resp);
        }

        if (actionType === THGActionType.INFOMAIL) {
            const payload: THGInfoMailPayload = {
                pretend: pretend,
                filter_params: filterParam.value,
                action_type: chosenAction.value,
                mail_body: '',
                mail_tag: '2025-payoff-iban-reminder',
            };

            // trigger action via api
            const resp = await triggerInfoMailSending(payload);

            return processActionResponse(resp);
        }

        if (actionType === THGActionType.BULK_ADD_TAGS) {
            if (selectedTags.value.length === 0) {
                throw new Error('No tags selected.');
            }

            const payload: THGUserAddTagsPayload = {
                pretend: pretend,
                filter_params: filterParam.value,
                action_type: THGActionType.BULK_ADD_TAGS,
                tags: selectedTags.value.map((tag) => tag._id!),
            };

            // trigger action via api
            const resp = await triggerAddTags(payload);

            return processActionResponse(resp);
        }
        throw new Error('unknown action type chosen.');
    }

return { __sfc: true,store, currentCountry, filterParam, chosenAction, selectedTags, enableActionButton, apply, triggerAction, THGActionType, Country }
}

})